import React, { useContext, useState, useEffect } from "react"
import { Button, Modal, Form, Alert, ListGroup } from "react-bootstrap"
import { useHistory } from "react-router-dom"
import Auth from "../utils/auth"
import request from "../utils/request"
import "../styles/AdminUsers.css"

const AdminUsers = () => {
  const { authUser } = useContext(Auth)
  const history = useHistory()

  const [adminUsers, setAdminUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newAdminEmail, setNewAdminEmail] = useState("")
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState(null)
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false)
  const [selectedUserToDelete, setSelectedUserToDelete] = useState(null)
  const [deleting, setDeleting] = useState(false)

  // Redirect if not logged in
  useEffect(() => {
    if (!authUser) {
      history.push("/dashboard/login")
    }
  }, [authUser, history])

  // Fetch admin users
  useEffect(() => {
    const fetchAdminUsers = async () => {
      try {
        setLoading(true)
        const options = {
          method: "GET",
        }
        const result = await request("/api/admin/users", options)
        setAdminUsers(result)
        setError(null)
      } catch (err) {
        console.error("Error fetching admin users:", err)
        setError("Failed to fetch admin users")
      } finally {
        setLoading(false)
      }
    }

    fetchAdminUsers()
  }, [])

  const handleCreateAdmin = async () => {
    if (!newAdminEmail.trim()) {
      setCreateError("Please enter an email address")
      return
    }

    try {
      setCreating(true)
      setCreateError(null)
      const options = {
        body: { email: newAdminEmail },
      }
      const result = await request("/api/admin/create-user", options)
      setAdminUsers([...adminUsers, result])
      setNewAdminEmail("")
      setShowCreateModal(false)
    } catch (err) {
      console.error("Error creating admin user:", err)
      setCreateError("Failed to create admin user. Please try again.")
    } finally {
      setCreating(false)
    }
  }

  const handleDeleteConfirm = (user) => {
    setSelectedUserToDelete(user)
    setDeleteConfirmModal(true)
  }

  const handleDeleteAdmin = async () => {
    if (!selectedUserToDelete) return

    try {
      setDeleting(true)
      const options = {
        body: { userId: selectedUserToDelete._id },
      }
      await request("/api/admin/delete-user", options)
      setAdminUsers(
        adminUsers.filter((u) => u._id !== selectedUserToDelete._id),
      )
      setDeleteConfirmModal(false)
      setSelectedUserToDelete(null)
    } catch (err) {
      console.error("Error deleting admin user:", err)
      setError("Failed to delete admin user")
    } finally {
      setDeleting(false)
    }
  }

  if (!authUser) {
    return null
  }

  const isOwner = authUser.role === "owner"

  return (
    <div className="admin-users-container">
      <div className="admin-users-header">
        <h2>Admin Users</h2>
        {isOwner && (
          <Button
            variant="primary"
            onClick={() => setShowCreateModal(true)}
            className="create-admin-btn"
          >
            + Create Admin User
          </Button>
        )}
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="loading">Loading admin users...</div>
      ) : adminUsers.length === 0 ? (
        <div className="no-users">No admin users found</div>
      ) : (
        <ListGroup className="admin-users-list">
          {adminUsers.map((user) => (
            <ListGroup.Item key={user._id} className="admin-user-item">
              <div className="user-info">
                <div className="user-email">{user.email}</div>
                <div className="user-role">{user.role}</div>
              </div>
              {isOwner && user.role === "admin" && (
                <button
                  className="delete-btn"
                  onClick={() => handleDeleteConfirm(user)}
                  title="Delete user"
                >
                  🗑️
                </button>
              )}
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}

      {/* Create Admin Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create New Admin User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {createError && <Alert variant="danger">{createError}</Alert>}
          <Form>
            <Form.Group>
              <Form.Label>Email Address</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email address"
                value={newAdminEmail}
                onChange={(e) => setNewAdminEmail(e.target.value)}
                disabled={creating}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowCreateModal(false)
              setNewAdminEmail("")
              setCreateError(null)
            }}
            disabled={creating}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateAdmin}
            disabled={creating}
          >
            {creating ? "Creating..." : "Create Admin"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={deleteConfirmModal}
        onHide={() => setDeleteConfirmModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete{" "}
          <strong>{selectedUserToDelete?.email}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setDeleteConfirmModal(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteAdmin}
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default AdminUsers
